/**
 * loginQuotes — rotating quotes for the login, signup, and institutional
 * screens. Quotes are now served from the CMS collection `platform_quotes`
 * (managed via the /admin "Quotes CMS" tab).
 *
 * The hardcoded arrays below stay as a graceful-degrade fallback so the
 * app is never quoteless if the API is unreachable or the collection
 * empty (first boot). On module import we fire three background fetches
 * to `/api/quotes/{deck}` and mutate the local arrays in place; the
 * `next*` helpers keep their synchronous signature so consumer pages
 * (Login, Register, BrokerSignup) need no changes.
 *
 * Rotation state persists in localStorage (`md:login-quote-idx`,
 * `md:signup-quote-idx`, `md:inst-quote-idx`).
 */
export const LOGIN_QUOTES = [
    { text: "The market never rewards emotion. Only discipline.",                             author: "Melian Dialogue" },
    { text: "You don't beat the market. You beat yourself.",                                  author: "Melian Dialogue" },
    { text: "Every trade is a mirror. Read it.",                                              author: "Melian Dialogue" },
    { text: "Africa's markets don't need bigger bets. They need better traders.",             author: "Melian Dialogue" },
    { text: "Discipline compounds faster than capital.",                                      author: "Melian Dialogue" },
    { text: "Be fearful when others are greedy. Greedy when others are fearful.",             author: "Warren Buffett" },
    { text: "In investing, what is comfortable is rarely profitable.",                        author: "Robert Arnott" },
    { text: "Know what you own, and know why you own it.",                                    author: "Peter Lynch" },
    { text: "The biggest risk of all is not taking one.",                                     author: "Mellody Hobson" },
    { text: "The individual investor should act as an investor, not a speculator.",           author: "Ben Graham" },
];

/**
 * nextLoginQuote — advances the rotation index by 1 (mod total), persists
 * to localStorage, and returns the newly-selected quote. Safe if
 * localStorage is unavailable (private mode, SSR).
 */
export function nextLoginQuote() {
    const KEY = "md:login-quote-idx";
    let idx = 0;
    try {
        const raw = window.localStorage.getItem(KEY);
        idx = raw ? (parseInt(raw, 10) + 1) : 0;
        if (!Number.isFinite(idx) || idx < 0) idx = 0;
        idx = idx % LOGIN_QUOTES.length;
        window.localStorage.setItem(KEY, String(idx));
    } catch (e) {
        idx = Math.floor(Math.random() * LOGIN_QUOTES.length);
    }
    return LOGIN_QUOTES[idx];
}

/**
 * SIGNUP_QUOTES — a curated, beginner-friendly subset for the Register
 * page. Focus is on knowledge, courage, discipline, and long-term
 * mindset — the messages a first-time trader benefits from most.
 * Skips the doom-loop warning quotes (Soros on losses, Templeton on
 * "it's different this time", etc.) which better fit returning users.
 */
export const SIGNUP_QUOTES = [
    { text: "Start small. Trade smart. Learn everything.",                                    author: "Melian Dialogue" },
    { text: "The best time to learn markets was before you had money in them.",               author: "Melian Dialogue" },
    { text: "Virtual capital. Real skill. Zero regret.",                                      author: "Melian Dialogue" },
    { text: "Africa deserves traders who know themselves before they know the market.",       author: "Melian Dialogue" },
    { text: "An investment in knowledge pays the best interest.",                             author: "Benjamin Franklin" },
    { text: "The biggest risk of all is not taking one.",                                     author: "Mellody Hobson" },
    { text: "Know what you own, and know why you own it.",                                    author: "Peter Lynch" },
    { text: "Discipline is the bridge between goals and accomplishment.",                     author: "Jim Rohn" },
    { text: "Every expert was once a beginner. Every trader was once someone new.",           author: "Melian Dialogue" },
    { text: "Habits, not headlines, build wealth.",                                           author: "Melian Dialogue" },
];

/**
 * nextSignupQuote — same auto-rotation semantics as nextLoginQuote, but
 * from the beginner-friendly SIGNUP_QUOTES deck. Separate localStorage
 * key so the login rotation and signup rotation don't interfere.
 */
export function nextSignupQuote() {
    const KEY = "md:signup-quote-idx";
    let idx = 0;
    try {
        const raw = window.localStorage.getItem(KEY);
        idx = raw ? (parseInt(raw, 10) + 1) : 0;
        if (!Number.isFinite(idx) || idx < 0) idx = 0;
        idx = idx % SIGNUP_QUOTES.length;
        window.localStorage.setItem(KEY, String(idx));
    } catch (e) {
        idx = Math.floor(Math.random() * SIGNUP_QUOTES.length);
    }
    return SIGNUP_QUOTES[idx];
}

/**
 * INSTITUTIONAL_QUOTES — the third deck, weighted for institutional
 * decision-makers (CMA brokers + MoE administrators). Focus is on
 * leadership, long-horizon thinking, fiduciary discipline, and the
 * mindset that separates an *institutional* investor from a speculator.
 * Renders on `/for-brokers`.
 */
export const INSTITUTIONAL_QUOTES = [
    { text: "Institutions build the market. Discipline builds the institution.",              author: "Melian Dialogue" },
    { text: "Serve the trader. The returns follow.",                                          author: "Melian Dialogue" },
    { text: "Africa's next capital pool won't come from abroad. It will come from within.",    author: "Melian Dialogue" },
    { text: "Wide diversification is only required when investors do not understand what they are doing.", author: "Warren Buffett" },
    { text: "The individual investor should act as an investor, not a speculator.",           author: "Ben Graham" },
    { text: "Know what you own, and know why you own it.",                                    author: "Peter Lynch" },
    { text: "The four most dangerous words in investing are, 'it's different this time.'",   author: "Sir John Templeton" },
    { text: "Returns matter a lot. It's our capital.",                                        author: "Abigail Johnson" },
    { text: "An investment in knowledge pays the best interest.",                             author: "Benjamin Franklin" },
    { text: "Behavioral edge is the last edge left.",                                         author: "Melian Dialogue" },
];

/**
 * nextInstitutionalQuote — rotation for the institutional deck.
 * Distinct localStorage key `md:inst-quote-idx` so it doesn't collide
 * with login or signup rotations.
 */
export function nextInstitutionalQuote() {
    const KEY = "md:inst-quote-idx";
    let idx = 0;
    try {
        const raw = window.localStorage.getItem(KEY);
        idx = raw ? (parseInt(raw, 10) + 1) : 0;
        if (!Number.isFinite(idx) || idx < 0) idx = 0;
        idx = idx % INSTITUTIONAL_QUOTES.length;
        window.localStorage.setItem(KEY, String(idx));
    } catch (e) {
        idx = Math.floor(Math.random() * INSTITUTIONAL_QUOTES.length);
    }
    return INSTITUTIONAL_QUOTES[idx];
}


/**
 * CMS bootstrap — fetches admin-managed decks from the backend and
 * mutates the in-memory arrays. Fires once on module import; failures
 * are silent (we already have hardcoded fallbacks above).
 *
 * The endpoint is public (no auth) so it works on /login and /register
 * before any session exists.
 */
const DECK_BINDINGS = [
    ["login",         LOGIN_QUOTES],
    ["signup",        SIGNUP_QUOTES],
    ["institutional", INSTITUTIONAL_QUOTES],
];

function _bootstrapQuotesFromServer() {
    if (typeof window === "undefined") return;
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    DECK_BINDINGS.forEach(([deck, target]) => {
        fetch(`${base}/api/quotes/${deck}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!data || !Array.isArray(data.quotes) || data.quotes.length === 0) return;
                // Replace contents of the exported array without changing
                // its identity (consumer imports hold a reference).
                target.splice(0, target.length, ...data.quotes.map((q) => ({
                    text:   q.text,
                    author: q.author,
                })));
            })
            .catch(() => { /* offline / API down — keep fallback deck */ });
    });
}

_bootstrapQuotesFromServer();
